/**
 * 前端轻量日志上报模块。
 *
 * 设计：
 * - 内存队列（max 20 条），三触发条件批量上报：
 *   1. 队列满 10 条 → 立即发送
 *   2. 距上次发送 >= 5s → 定时检查
 *   3. beforeunload → sendBeacon 兜底（需配置 VITE_LOG_ENDPOINT）
 * - 同类型错误 10s 去重（按 message 前 100 字符）
 * - 上报失败静默丢弃，不重试
 * - 只采集 warn / error 级别
 *
 * 上报方式：
 * - 优先通过 TCB SDK callFunction（与项目其他调用一致，无需额外配置）
 * - beforeunload 兜底用 sendBeacon（需配置 VITE_LOG_ENDPOINT）
 */
import type { LogEntry, LogLevel } from '@cms/shared'

const MAX_QUEUE = 20
const FLUSH_THRESHOLD = 10
const FLUSH_INTERVAL_MS = 5_000
const DEDUP_WINDOW_MS = 10_000
const DEDUP_KEY_LEN = 100

const queue: LogEntry[] = []
const dedupMap = new Map<string, number>() // key → last seen timestamp

let flushTimer: ReturnType<typeof setInterval> | null = null
let lastFlushTime = Date.now()
let logTransport: ((entries: LogEntry[]) => Promise<unknown>) | null = null

/** 注册日志传输实现，由 TCB 适配层在初始化时注入。 */
export function configureFrontendLogTransport(
  transport: (entries: LogEntry[]) => Promise<unknown>,
): void {
  logTransport = transport
}

/** 获取上报地址（仅用于 beforeunload 兜底，正常上报走 callFunction） */
function getEndpoint(): string {
  return (import.meta.env['VITE_LOG_ENDPOINT'] as string) || ''
}

/** 获取最低采集级别（默认 warn） */
function getMinLevel(): LogLevel {
  const raw = import.meta.env['VITE_LOG_LEVEL'] as string | undefined
  if (raw && ['debug', 'info', 'warn', 'error'].includes(raw)) return raw as LogLevel
  return 'warn'
}

/** 生成去重 key */
function dedupKey(msg: string): string {
  return msg.slice(0, DEDUP_KEY_LEN)
}

/** 检查是否应该去重 */
function shouldDedup(msg: string): boolean {
  const key = dedupKey(msg)
  const lastSeen = dedupMap.get(key)
  const now = Date.now()
  if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) {
    return true
  }
  dedupMap.set(key, now)
  return false
}

/** 清理过期的去重记录 */
function cleanDedupMap(): void {
  const now = Date.now()
  for (const [key, ts] of dedupMap) {
    if (now - ts > DEDUP_WINDOW_MS * 2) {
      dedupMap.delete(key)
    }
  }
}

/** 创建一条日志条目并加入队列 */
function enqueue(level: LogLevel, msg: string, meta?: Record<string, unknown>): void {
  // 级别过滤
  const minLevel = getMinLevel()
  const levelOrder: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 }
  if (levelOrder[level] < levelOrder[minLevel]) return

  // 去重
  if (shouldDedup(msg)) return

  const entry: LogEntry = {
    level,
    ts: new Date().toISOString(),
    msg,
    // hash 路由下 location.pathname 恒为 '/', 用 hash 还原真实路由；history 模式回退 pathname
    route: window.location.hash ? window.location.hash.slice(1) : window.location.pathname,
    userAgent: navigator.userAgent,
    ...meta,
  }

  queue.push(entry)

  // 队列上限
  if (queue.length > MAX_QUEUE) {
    queue.splice(0, queue.length - MAX_QUEUE)
  }

  // 满阈值立即发送
  if (queue.length >= FLUSH_THRESHOLD) {
    flush()
  }
}

/** 批量上报队列中的日志 */
function flush(): void {
  if (queue.length === 0) return

  const entries = queue.splice(0, queue.length)
  lastFlushTime = Date.now()
  cleanDedupMap()

  if (!logTransport) {
    queue.unshift(...entries)
    return
  }

  // 通过 TCB SDK callFunction 上报，失败静默
  void logTransport(entries)
    .catch(() => {
      // 静默丢弃，不重试（callFunction 可能因未登录而失败，这是预期的）
    })
}

/** beforeunload 兜底：使用 sendBeacon */
function handleBeforeUnload(): void {
  if (queue.length === 0) return
  const endpoint = getEndpoint()
  if (!endpoint) return

  const blob = new Blob([JSON.stringify(queue)], { type: 'application/json' })
  navigator.sendBeacon(endpoint, blob)
  queue.length = 0
}

/** 启动定时器和 beforeunload 监听 */
function start(): void {
  if (flushTimer) return
  flushTimer = setInterval(() => {
    if (Date.now() - lastFlushTime >= FLUSH_INTERVAL_MS) {
      flush()
    }
  }, FLUSH_INTERVAL_MS)

  window.addEventListener('beforeunload', handleBeforeUnload)
}

/** 停止定时器（用于测试或页面卸载） */
function stop(): void {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
  window.removeEventListener('beforeunload', handleBeforeUnload)
}

// 自动启动
start()

/** 前端 Logger 接口（与后端 Logger 类型兼容） */
export interface FrontendLogger {
  debug(msg: string, meta?: Record<string, unknown>): void
  info(msg: string, meta?: Record<string, unknown>): void
  warn(msg: string, meta?: Record<string, unknown>): void
  error(msg: string, meta?: Record<string, unknown>): void
  child(context: Record<string, unknown>): FrontendLogger
}

/** 创建前端 Logger（只采集 warn/error，上报到云函数） */
export function createFrontendLogger(context?: Record<string, unknown>): FrontendLogger {
  return {
    debug(msg: string, meta?: Record<string, unknown>) {
      enqueue('debug', msg, { ...context, ...meta })
    },
    info(msg: string, meta?: Record<string, unknown>) {
      enqueue('info', msg, { ...context, ...meta })
    },
    warn(msg: string, meta?: Record<string, unknown>) {
      enqueue('warn', msg, { ...context, ...meta })
    },
    error(msg: string, meta?: Record<string, unknown>) {
      enqueue('error', msg, { ...context, ...meta })
    },
    child(extraContext: Record<string, unknown>) {
      return createFrontendLogger({ ...context, ...extraContext })
    },
  }
}

// 导出 flush 供测试或手动触发
export { flush, stop }
